/**
 * Layout decisions for the Profile page that MORE THAN ONE file has to agree on.
 *
 * Today that is exactly one decision — how the avatar block is aligned — and it
 * lives here rather than in `index.tsx` because `ProfileSkeleton` needs the same
 * value to keep footprint parity (working-conventions §19), and importing it
 * from the page would close a cycle (`index.tsx` renders `ProfileSkeleton`).
 */

/**
 * The avatar block is LEFT-ALIGNED, on the same axis as everything else.
 *
 * ## This has been proposed, built and rejected. Read before re-opening it.
 *
 * Centring the avatar was implemented and viewed at 375 and 1280 on 2026-08-23
 * and rejected on sight: *"Profile looks weird center when everything else is
 * left align"*. Two arguments decided it, and neither is about taste:
 *
 * 1. **It is a ROW, not a symbol.** `ProfileAvatar` renders the avatar (with an
 *    edit pencil overlapping its bottom-right corner) AND a QR button beside it.
 *    A cluster of two dissimilar controls has no clean optical centre, so
 *    centring the row's bounding box does not centre anything the eye reads as
 *    the subject. Measured: at 1280 the pair spanned 668–812 in a 420–1060
 *    column, so the pair's box was centred on the midline (740) while the
 *    AVATAR's own centre sat at 708 — 32px left of it (31px at 375). The block
 *    looked mis-centred rather than centred, which is why it read as "weird"
 *    rather than merely different.
 *
 * 2. **The argument proves too much.** If the avatar is special enough to be
 *    exempt from the left axis, so is the page title — and the title is
 *    left-aligned and stays that way. So the "identity vs data" register split
 *    was never being applied; only the avatar was being singled out. One
 *    exception against a dozen left-aligned rows is not a register, it is an
 *    outlier.
 *
 * Desktop makes it worse, not better: `SideNav` is a sticky rail, so the content
 * column's left edge is now a strong vertical line running the height of the
 * page, and a centred element inside that column reads as floating off it.
 *
 * ## The option that WOULD work, if this comes back
 *
 * Centring is only correct if the page commits to a full centred HEADER BAND —
 * avatar, name, and a membership/status line, all centred, visually separated
 * from the form beneath by a rule or a background change. That is the pattern
 * the phone apps being borrowed from actually use (they centre an identity
 * BLOCK, not a lone photo, and their settings list is a separate surface).
 *
 * A centred avatar above a left-aligned form is the half-version: it pays the
 * inconsistency cost without buying the focal payoff. So the real choice is
 * "left-aligned control" or "build the header band" — not "centre the photo".
 *
 * ## Mechanics
 *
 * Both `pages/Profile/index.tsx` and `components/pages/profile/ProfileSkeleton`
 * read this, so the loading state and the loaded page cannot disagree — which
 * they DID before this constant existed: the skeleton centred its avatar
 * placeholder while the real page rendered it left-aligned, so the block jumped
 * sideways the moment the profile arrived. Change it in one place or not at all.
 */
export const AVATAR_ALIGN: 'center' | 'flex-start' = 'flex-start'
